import type { MechanicDefinition } from "./types";

export const combat: MechanicDefinition = {
  id: "combat",
  disabled: true,
  name: "Combat",
  description: "Structured combat with initiative, attacks, HP tracking, and hit-location damage",
  icon: "Swords",
  category: "systems",
  auditPasses: 1, // ⭐
  aiHints: {
    purpose: "Provides structured turn-based combat resolution with HP, damage types, hit locations, and death rules.",
    whenToUse: "Any world where physical conflict is possible — fantasy, sci-fi, survival, action genres.",
    synergies: ["skill_checks", "conditions", "saving_throws", "character_classes", "inventory", "resting"],
    conflicts: [],
    prerequisites: [],
    tags: ["combat", "hp", "damage", "death", "tactical", "action"],
    complexity: 3,
    exampleConfig: { style: "narrative", lethality: "medium" },
    genrePresets: {
      "dark_fantasy": { style: "dice_crunch", lethality: "high" },
      "heroic_adventure": { style: "narrative", lethality: "low" },
      "tactical_rpg": { style: "tactical", lethality: "medium" },
      "survival_horror": { style: "dice_crunch", lethality: "brutal" },
    },
    stateVariables: ["combatants", "player_hp", "player_max_hp", "_last_combat_roll", "_last_combat_hit", "_last_combat_damage", "_defending", "_death_saves_player"],
  },
  configSchema: [
    {
      key: "style",
      label: "Combat Style",
      type: "select",
      options: [
        { value: "narrative", label: "Narrative (loose, story-driven)" },
        { value: "dice_crunch", label: "Dice Crunch (detailed rolls)" },
        { value: "tactical", label: "Tactical (positioning matters)" },
      ],
      default: "narrative",
      description: "How detailed combat encounters are",
    },
    {
      key: "lethality",
      label: "Lethality",
      type: "select",
      options: [
        { value: "low", label: "Low (heroes rarely die)" },
        { value: "medium", label: "Medium (balanced)" },
        { value: "high", label: "High (death is common)" },
        { value: "brutal", label: "Brutal (one mistake kills)" },
      ],
      default: "medium",
    },
  ],
  systemPromptSection: (config) => {
    const style = config.style || "narrative";
    const lethality = config.lethality || "medium";
    const styleDesc: Record<string, string> = {
      narrative: "Combat is story-driven. Describe actions cinematically. Use simple rolls to determine outcomes. Focus on drama over mechanics.",
      dice_crunch: "Combat uses detailed dice rolls: initiative (1d20+dex), attack rolls (1d20+str/dex vs AC), damage rolls. Track HP carefully.",
      tactical: "Combat considers positioning, cover, terrain advantages. Describe the tactical situation. Movement and positioning affect outcomes.",
    };
    const lethalityRules: Record<string, string> = {
      low: "At 0 HP: unconscious, auto-stabilize after 1 turn. Player rarely dies.",
      medium: "At 0 HP: unconscious, death_save required each turn (DC 10). 3 failures = death.",
      high: "At 0 HP: dead unless healed within 1 turn. No free saves.",
      brutal: "At 0 HP: dead immediately. No saves, no mercy.",
    };
    return `## COMBAT & HEALTH SYSTEM (Hard Mechanic)
- Style: ${style}
- Lethality: ${lethality}
- ${styleDesc[style]}

### DAMAGE PIPELINE (MANDATORY)
After every combat_action that HITS, you MUST call apply_damage to actually apply the damage:
  1. combat_action → determines if attack hits and rolls raw damage
  2. apply_damage → applies location multiplier, armor reduction, updates HP, checks death
  3. register_kill → call ONLY after apply_damage confirms HP ≤ 0

### HIT LOCATIONS
Choose location based on narrative context (aimed shots, random swings, etc.):
- head (1.5x damage) — devastating but hard to target
- torso (1.0x) — standard target
- arms (0.7x) — disabling but less lethal
- legs (0.8x) — mobility impairment
- general (1.0x) — when location is narratively unimportant

### DAMAGE TYPES
slashing, piercing, bludgeoning, fire, cold, poison, magical — for narrative flavor and potential resistances

### HP THRESHOLDS (auto-applied by apply_damage)
- >50% HP: Normal
- 25-50% HP: WOUNDED — impaired performance, narrative consequences
- 1-25% HP: CRITICAL — mechanical -2 to all checks, desperate situation
- 0 HP: ${lethalityRules[lethality]}
- Massive damage (negative HP > max_hp/2): Instant death regardless of lethality

### HEALING
Use the heal tool for potions, spells, rest recovery. Caps at max_hp.

### ACTION ECONOMY (per turn per combatant)
- 1 Action (attack, cast spell, dodge, dash, disengage, help, hide, use object)
- 1 Bonus Action (offhand attack, some spells, class features)
- 1 Reaction (opportunity attack, shield spell, counterspell)
- Movement (30ft default, modified by race/conditions/armor)
- Extra Attack: Fighters/Rangers/Paladins at level 5+ get 2 attacks per Action.
- Opportunity Attacks: Use Reaction when enemy leaves melee range without Disengage.

### CONCENTRATION & RESISTANCES
- If a caster is concentrating and takes damage → CON save DC = max(10, floor(damage/2)). Fail = spell ends.
- Only ONE concentration spell at a time. Starting a new one ends the old.
- Before applying damage, check damage_resistances → halve. Check damage_vulnerabilities → double. Check damage_immunities → 0.

### COMBAT FLOW
- When combat begins, establish initiative order and set combatants array
- Track HP on ALL combatants: {name, hp, max_hp, ac, is_player, conditions}
- Describe combat vividly with consequences for both hits and misses
- Combat ends when enemies are defeated, flee, or the player negotiates`;
  },
  widgets: [
    {
      id: "combat_tab",
      type: "sidebar_tab",
      label: "Combat",
      icon: "Swords",
      component: "CombatPanel",
      dataBindings: { combatants: "variables.combatants" },
      visibility: "when_data",
      priority: 20,
    },
    {
      id: "combat_inline",
      type: "inline",
      label: "Combat",
      icon: "Swords",
      component: "CombatPanel",
      dataBindings: { combatants: "variables.combatants" },
      visibility: "when_data",
      priority: 10,
    },
  ],
  initializeState: () => ({
    combatants: [],
    player_hp: 20,
    player_max_hp: 20,
  }),
  tools: [
    {
      type: "function",
      function: {
        name: "combat_action",
        description: "Process a combat action: attack, defend, flee, or special ability. After a HIT, you MUST call apply_damage next.",
        parameters: {
          type: "object",
          properties: {
            action_type: {
              type: "string",
              enum: ["attack", "defend", "flee", "special"],
              description: "Type of combat action",
            },
            attacker: { type: "string", description: "Who is acting" },
            target: { type: "string", description: "Target of the action" },
            stat_used: { type: "string", description: "Which stat modifier to use (STR for melee, DEX for ranged)" },
            target_ac: { type: "number", description: "Target's armor class" },
            damage_dice: { type: "string", description: "Damage expression if hit, e.g. '1d8+3'" },
            details: { type: "string", description: "Specific details of the action" },
          },
          required: ["action_type", "attacker", "target"],
        },
      },
    },
    {
      type: "function",
      function: {
        name: "apply_damage",
        description: "MANDATORY after every combat_action hit. Applies location-based damage, armor reduction, and updates HP. Returns new HP status and wound conditions.",
        parameters: {
          type: "object",
          properties: {
            target: { type: "string", description: "Entity name or 'player'" },
            damage: { type: "number", description: "Raw damage from combat_action" },
            location: {
              type: "string",
              enum: ["head", "torso", "arms", "legs", "general"],
              description: "Hit location — affects damage multiplier",
            },
            damage_type: {
              type: "string",
              enum: ["slashing", "piercing", "bludgeoning", "fire", "cold", "poison", "magical"],
              description: "Type of damage for narrative and resistance purposes",
            },
            source: { type: "string", description: "What caused the damage (weapon name, spell, etc.)" },
          },
          required: ["target", "damage", "location"],
        },
      },
    },
    {
      type: "function",
      function: {
        name: "heal",
        description: "Heal a target. Restores HP (capped at max), removes wound conditions when above thresholds.",
        parameters: {
          type: "object",
          properties: {
            target: { type: "string", description: "Entity name or 'player'" },
            amount: { type: "number", description: "HP to restore" },
            source: { type: "string", description: "Healing source: potion, spell, rest, etc." },
          },
          required: ["target", "amount", "source"],
        },
      },
    },
    {
      type: "function",
      function: {
        name: "death_save",
        description: "Roll a death saving throw for a character at 0 HP. Only used when lethality is 'medium'. DC 10, 3 failures = death, 3 successes = stabilized.",
        parameters: {
          type: "object",
          properties: {
            target: { type: "string", description: "Entity name or 'player'" },
          },
          required: ["target"],
        },
      },
    },
    {
      type: "function",
      function: {
        name: "register_kill",
        description: "Call ONLY when apply_damage confirms HP ≤ 0 and the target is dead. Awards XP.",
        parameters: {
          type: "object",
          properties: {
            target: { type: "string", description: "Name of the killed entity" },
            xp_value: { type: "number", description: "XP reward (10-100)" },
            cause: { type: "string", description: "How they died" },
          },
          required: ["target", "xp_value"],
        },
      },
    },
  ],
};
